import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronUp, UserPlus } from 'lucide-react';
import { useAddTeamModal } from './AddTeamModalProvider.js';
import { FIRST_STAFF_TARGET, useActivationStatus } from './activation.js';

/**
 * A slim bar over every screen, in the browser and the phone app alike, while the company has
 * fewer than three employees besides the owner: "Add your first 3 staff members to start
 * attendance", opening the shell's Add your team modal. It cannot be turned off for good (the
 * owner asked for it to stay until the team is in); the collapse button only hides it on the
 * current screen. Hidden on /welcome and while the modal is open. It sits under the sticky
 * topbar, which already pads for the phone's status bar (safe-area inset).
 */
export function AddFirstStaffBar() {
  const { pathname } = useLocation();
  const status = useActivationStatus();
  const addTeam = useAddTeamModal();
  const [collapsedOn, setCollapsedOn] = useState<string | null>(null);

  if (!status || !addTeam || addTeam.isOpen) return null;
  if (status.teamSize >= FIRST_STAFF_TARGET) return null;
  if (pathname.startsWith('/welcome') || collapsedOn === pathname) return null;

  const left = FIRST_STAFF_TARGET - status.teamSize;

  return (
    <div
      role="region"
      aria-label="Add your first staff"
      className="flex items-center gap-1 border-b border-border bg-accent-subtle pr-2 text-sm text-fg sm:pr-4"
    >
      <button
        type="button"
        onClick={() => addTeam.open()}
        className="flex min-h-11 min-w-0 flex-1 items-center gap-3 py-2 pl-4 text-left transition-colors hover:bg-surface/50 sm:pl-6"
      >
        <UserPlus className="size-4 shrink-0 text-accent" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="font-semibold">
            Add your first {FIRST_STAFF_TARGET} staff members to start attendance
          </span>
          {status.teamSize > 0 && <span className="text-fg-muted"> · {left} more to go</span>}
        </span>
        <span className="shrink-0 rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-fg-on-accent">
          Add staff
        </span>
      </button>
      <button
        type="button"
        onClick={() => setCollapsedOn(pathname)}
        aria-label="Hide on this page"
        title="Hide on this page"
        className="flex size-10 shrink-0 items-center justify-center rounded-full text-fg-muted transition-colors hover:bg-surface hover:text-fg sm:size-8"
      >
        <ChevronUp className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}
